/**
 * Quick-suggestion chips — deterministic entity → proposal mapping.
 *
 * When a chat thread references a known entity (the conversation's `recent`
 * pointer, or an entity a question resolved), the UI shows 2–3 contextual
 * chips ("Create follow-up task", "Log a note"). Each chip PREFILLS a real
 * `AgentAction` proposal that still routes through the confirm-each-write
 * gate — a chip never runs anything by itself.
 *
 * Pure and store-free so it is trivially testable, and honest by
 * construction: suggestions only appear when the referenced entity still
 * resolves in the live context — a stale/deleted reference yields [] rather
 * than a fabricated proposal.
 */

import type { AgentAction, AgentContext } from './types';

export interface QuickSuggestion {
  /** Chip label shown to the operator. */
  label: string;
  /** The prefilled proposal — pending until explicitly confirmed. */
  action: AgentAction;
}

/** Plain ISO date (YYYY-MM-DD) `n` days from today, local time. */
function inDays(n: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Map the conversation's last-referenced entity to 2–3 one-click proposal
 * chips. Returns [] when there is no entity, or when the entity no longer
 * exists in the live context (never suggest a write against a ghost).
 */
export function quickSuggestionsFor(
  recent: AgentContext['recent'] | undefined,
  ctx: Pick<AgentContext, 'people' | 'accounts'>,
): QuickSuggestion[] {
  if (!recent) return [];

  if (recent.personId) {
    const person = ctx.people.find((p) => p.id === recent.personId);
    if (!person) return [];
    const chips: QuickSuggestion[] = [
      {
        label: 'Create follow-up task',
        action: {
          kind: 'create_task',
          title: `Follow up with ${person.name}`,
          priority: 'high',
          dueDate: inDays(3),
          personId: person.id,
          personName: person.name,
        },
      },
      {
        label: 'Log a note',
        action: {
          kind: 'log_activity',
          personId: person.id,
          personName: person.name,
          activityKind: 'note',
          text: `Reviewed ${person.name} in chat — follow-up needed.`,
        },
      },
    ];
    // Open prospects get the CRM-reminder chip too (closed deals don't need one).
    if (
      person.category === 'prospect' &&
      person.stage !== 'closed-won' &&
      person.stage !== 'closed-lost'
    ) {
      chips.push({
        label: 'Schedule follow-up',
        action: {
          kind: 'schedule_follow_up',
          personId: person.id,
          personName: person.name,
          followUpDate: inDays(3),
          note: 'Follow up (scheduled from chat)',
        },
      });
    }
    return chips.slice(0, 3);
  }

  if (recent.accountId) {
    const acct = ctx.accounts.find((a) => a.id === recent.accountId);
    if (!acct) return [];
    return [
      {
        label: 'Create follow-up task',
        action: {
          kind: 'create_task',
          title: `Follow up on ${acct.name}`,
          priority: 'high',
          dueDate: inDays(3),
          accountId: acct.id,
          accountName: acct.name,
        },
      },
      {
        label: 'Draft a proposal',
        action: {
          kind: 'create_document',
          docKind: 'proposal',
          entityType: 'account',
          entityId: acct.id,
          entityName: acct.name,
          contactName: acct.pocName,
          ownerName: acct.ownerName,
        },
      },
    ];
  }

  return [];
}
